// Worker API 客户端 — HTTP 路由封装

import type { RoomCreateRequest, RoomCreateResponse, RoomInspectResponse, Friendship, FriendRequest, ChatMessage } from '@kuaichuan/shared';
import { workerUrl, workerWsUrl } from './config';

// 统一解析响应：兼容 JSON / 空响应 / 网络错误，返回友好错误信息
async function parseJson<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  if (!text) {
    throw new Error(resp.ok ? '服务器返回空响应' : `请求失败 (${resp.status})`);
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`服务器响应格式错误 (${resp.status})`);
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(workerUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('无法连接服务器，请检查网络或稍后重试');
  }
  return parseJson<T>(resp);
}

async function getJson<T>(path: string): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(workerUrl(path));
  } catch {
    throw new Error('无法连接服务器，请检查网络或稍后重试');
  }
  return parseJson<T>(resp);
}

export interface ApiResult<T> {
  success: boolean;
  message?: string;
  data?: T;
}

// ===== 房间 API =====

export async function createRoom(req: RoomCreateRequest): Promise<RoomCreateResponse & { pin?: string }> {
  const result = await postJson<ApiResult<RoomCreateResponse & { pin?: string }>>('/api/rooms', req);
  if (!result.success || !result.data) throw new Error(result.message ?? '创建房间失败');
  return result.data;
}

export async function inspectRoom(code: string): Promise<RoomInspectResponse> {
  const result = await getJson<ApiResult<RoomInspectResponse>>(`/api/rooms/${code}`);
  if (!result.success || !result.data) throw new Error(result.message ?? '房间不存在');
  return result.data;
}

export async function verifyPin(code: string, pin: string): Promise<boolean> {
  const result = await postJson<{ valid: boolean }>(`/api/rooms/${code}/verify-pin`, { pin });
  return result.valid;
}

// ===== 健康检查 =====

export async function checkHealth(): Promise<{ ok: boolean; app: string; version: string; time: string }> {
  return getJson('/api/health');
}

// ===== 配置拉取 =====

export async function fetchConfig(): Promise<{
  app: string;
  protocolVersion: string;
  stunServers: string[];
  roomTtlSeconds: number;
  workerOrigin: string;
  appId: string;
  unifiedAuthBase: string;
}> {
  return getJson('/api/config');
}

// ===== 鉴权 API（走 worker 代理，APP_SECRET 在服务端注入，不暴露给前端）=====
// 来源: 统一管理平台api文档.txt 第 3-8 节

export interface AuthUser {
  id: string;
  email: string;
  nickname?: string;
}

interface AuthResult {
  success: boolean;
  message?: string;
  data?: { user?: AuthUser; token?: string; is_new_user?: boolean; exists?: boolean; has_password?: boolean };
}

// 3. 查询用户是否已注册
export async function checkUser(email: string): Promise<{ exists: boolean; hasPassword: boolean }> {
  const r = await postJson<AuthResult>('/api/auth/check-user', { email });
  return {
    exists: !!r.data?.exists,
    hasPassword: !!r.data?.has_password,
  };
}

// 4. 发送验证码
export async function sendCode(email: string): Promise<{ success: boolean; message?: string }> {
  const r = await postJson<AuthResult>('/api/auth/send-code', { email });
  return { success: r.success, message: r.message };
}

// 5. 注册（邮箱 + 验证码 + 密码，无用户名）
export async function register(
  email: string,
  code: string,
  password: string,
  nickname?: string,
): Promise<{ success: boolean; message?: string; user?: AuthUser; token?: string }> {
  const r = await postJson<AuthResult>('/api/auth/register', { email, code, password, nickname });
  return { success: r.success, message: r.message, user: r.data?.user, token: r.data?.token };
}

// 6. 密码登录
export async function login(
  email: string,
  password: string,
): Promise<{ success: boolean; message?: string; user?: AuthUser; token?: string }> {
  const r = await postJson<AuthResult>('/api/auth/login', { email, password });
  return { success: r.success, message: r.message, user: r.data?.user, token: r.data?.token };
}

// 7. 验证码登录（免密码，用户不存在则自动注册）
export async function verifyLogin(
  email: string,
  code: string,
): Promise<{ success: boolean; message?: string; user?: AuthUser; token?: string; isNewUser?: boolean }> {
  const r = await postJson<AuthResult>('/api/auth/verify-login', { email, code });
  return {
    success: r.success,
    message: r.message,
    user: r.data?.user,
    token: r.data?.token,
    isNewUser: r.data?.is_new_user,
  };
}

// 8. 找回/重置密码
export async function resetPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<{ success: boolean; message?: string }> {
  const r = await postJson<AuthResult>('/api/auth/reset-password', { email, code, new_password: newPassword });
  return { success: r.success, message: r.message };
}

// 当前用户信息（用本地 token 校验）
export async function fetchMe(token: string): Promise<AuthUser | null> {
  let resp: Response;
  try {
    resp = await fetch(workerUrl('/api/auth/me'), {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    return null;
  }
  const r = await parseJson<AuthResult>(resp);
  if (r.success && r.data?.user) return r.data.user;
  return null;
}

// 登出（token 加入黑名单）
export async function logout(token: string): Promise<void> {
  try {
    await fetch(workerUrl('/api/auth/logout'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    /* ignore */
  }
}

// ===== 带 Bearer Token 的请求 =====

async function authGetJson<T>(path: string, token: string): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(workerUrl(path), { headers: { Authorization: `Bearer ${token}` } });
  } catch {
    throw new Error('无法连接服务器');
  }
  return parseJson<T>(resp);
}

async function authPostJson<T>(path: string, body: unknown, token: string): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(workerUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('无法连接服务器');
  }
  return parseJson<T>(resp);
}

// ===== 好友 API =====

export async function getFriends(token: string): Promise<Friendship[]> {
  const r = await authGetJson<{ success: boolean; friends: Friendship[] }>('/api/friends/list', token);
  return r.friends ?? [];
}

export async function getFriendRequests(token: string): Promise<FriendRequest[]> {
  const r = await authGetJson<{ success: boolean; requests: FriendRequest[] }>('/api/friends/requests', token);
  return r.requests ?? [];
}

export async function addFriend(token: string, target: { email?: string; userId?: string; message?: string }): Promise<{ success: boolean; message?: string }> {
  return authPostJson('/api/friends/add', target, token);
}

export async function acceptFriend(
  token: string,
  req: { requestId: string; fromId: string; fromEmail: string; fromName: string },
): Promise<{ success: boolean; message?: string }> {
  return authPostJson('/api/friends/accept', req, token);
}

export async function rejectFriend(token: string, requestId: string): Promise<{ success: boolean; message?: string }> {
  return authPostJson('/api/friends/reject', { requestId }, token);
}

export async function removeFriend(token: string, friendId: string): Promise<{ success: boolean; message?: string }> {
  return authPostJson('/api/friends/remove', { friendId }, token);
}

// ===== 聊天 API =====

export async function getChatHistory(token: string, peerId: string, before?: number, limit = 50): Promise<ChatMessage[]> {
  const params = new URLSearchParams({ peerId, limit: String(limit) });
  if (before) params.set('before', String(before));
  const r = await authGetJson<{ success: boolean; messages: ChatMessage[] }>(`/api/chat/history?${params}`, token);
  return r.messages ?? [];
}

/** 构造聊天 WebSocket URL（token 通过 query param 传递，浏览器 WS 不支持自定义 header） */
export function chatWsUrl(peerId: string, token: string): string {
  return workerWsUrl(`/api/chat/ws?peerId=${encodeURIComponent(peerId)}&token=${encodeURIComponent(token)}`);
}
